import { DeltaAction, DeltaStatus, type DeltaResponse } from '../../api';
import { stripCpf } from '../../format';

/**
 * The roster rules the customer portal and the staff company page share.
 *
 * Both screens answer "who is insured here, and is anything about to happen to
 * them?", and the two answers must not drift: a person marked "Exclusão em
 * 31/03" in the portal and unmarked on the staff page is a phone call nobody
 * can settle. So the search and the reading of scheduled changes live here,
 * once, and each area brings only its own copy.
 */

export const ACTION_LABELS: Record<DeltaAction, string> = {
  [DeltaAction.ADMISSION]: 'Inclusão',
  [DeltaAction.TERMINATION]: 'Exclusão',
  [DeltaAction.SALARY_CHANGE]: 'Alteração salarial',
  [DeltaAction.LEAVE]: 'Afastamento',
};

// --- Searching the roster ---------------------------------------------------

/**
 * Accents off, case flattened, so "Joao" finds "João".
 *
 * A person typing a name into a search box is not going to reach for the
 * circumflex, and a roster that only matches the accented spelling is a roster
 * people give up on and phone about instead.
 */
function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * What a row needs for the search to find it: a contract's roster item and a
 * company's population item both have these two, and nothing else is read.
 */
export interface SearchablePerson {
  readonly cpf: string;
  readonly full_name: string;
}

/**
 * Whether one insured person matches what was typed.
 *
 * A query with digits in it is matched against the CPF as digits, so
 * `123.456` and `123456` behave the same; anything else is matched against the
 * name. A query that is partly both matches either, because guessing which the
 * person meant is worse than showing them both.
 */
export function matchesSearch(person: SearchablePerson, query: string): boolean {
  if (query.trim() === '') {
    return true;
  }
  return matchesCpf(person, query) || matchesName(person, query);
}

/**
 * Whether the digits typed are in the person's CPF, punctuation aside on both
 * sides. Nothing typed matches everybody; typing with no digit matches nobody.
 */
export function matchesCpf(person: SearchablePerson, query: string): boolean {
  if (query.trim() === '') {
    return true;
  }
  const digits = stripCpf(query);
  return digits !== '' && stripCpf(person.cpf).includes(digits);
}

/**
 * Whether what was typed is part of the person's name, accents and case aside.
 * Nothing typed matches everybody.
 */
export function matchesName(person: SearchablePerson, query: string): boolean {
  if (query.trim() === '') {
    return true;
  }
  const text = normalise(query);
  // A query of pure punctuation and digits has nothing to say about a name.
  return text.replace(/[^a-z]/g, '') === '' ? false : normalise(person.full_name).includes(text);
}

export function searchRoster<T extends SearchablePerson>(
  people: readonly T[],
  query: string,
): readonly T[] {
  return query.trim() === '' ? people : people.filter((person) => matchesSearch(person, query));
}

// --- What counts as scheduled ----------------------------------------------

/**
 * A change that has been asked for and has not happened yet.
 *
 * Two quite different things qualify, and both have to be visible for the same
 * reason -- the person looks ordinary in today's roster:
 *
 *  - **Pending**, whatever its date. Nobody has decided it, so nothing is in
 *    the roster. This is the one that stops an HR user who filed a termination
 *    in March from filing it again in April.
 *  - **Approved, but dated after today.** This one *is* in the roster already:
 *    #210 writes an approved delta immediately with its own effective date, and
 *    the projection keeps reporting the person as insured until the date
 *    arrives. Without this they would look like everybody else right up to the
 *    day they disappear.
 *
 * Rejected and cancelled changes are not scheduled. They are history.
 */
export function isScheduled(delta: DeltaResponse, today: string): boolean {
  if (delta.status === DeltaStatus.SUBMITTED) {
    return true;
  }
  return delta.status === DeltaStatus.APPROVED && delta.effective_date > today;
}

export function scheduledChanges(
  deltas: readonly DeltaResponse[],
  today: string,
): readonly DeltaResponse[] {
  const seen = new Set<string>();
  const scheduled: DeltaResponse[] = [];
  for (const delta of deltas) {
    if (!isScheduled(delta, today) || seen.has(delta.id)) {
      continue;
    }
    seen.add(delta.id);
    scheduled.push(delta);
  }
  // Soonest first: the question this list answers is "what is about to happen?".
  return scheduled.toSorted((a, b) => a.effective_date.localeCompare(b.effective_date));
}

/** The scheduled changes of each person, keyed by the canonical eleven digits. */
export function scheduledByCpf(
  deltas: readonly DeltaResponse[],
): ReadonlyMap<string, readonly DeltaResponse[]> {
  const byCpf = new Map<string, DeltaResponse[]>();
  for (const delta of deltas) {
    const key = stripCpf(delta.cpf);
    const existing = byCpf.get(key);
    if (existing === undefined) {
      byCpf.set(key, [delta]);
    } else {
      existing.push(delta);
    }
  }
  return byCpf;
}

/**
 * The one scheduled change a roster row speaks for.
 *
 * A person can have more than one thing coming -- a salary change in the middle
 * of the month, an afastamento from the 6th and a termination at the end of it
 * -- and the row has space for one. A termination wins, because it is the change
 * that ends the coverage the row is describing; among changes of equal standing,
 * the soonest wins, because that is the one about to happen. An afastamento is
 * of equal standing rather than ahead of the others: it does not end anything,
 * and a row announcing an absence while an exclusion is pending would be saying
 * the smaller of the two things.
 *
 * **It returns the change, not a label.** Returning a word and leaving the
 * caller to find a date separately is exactly how a row comes to read
 * "Exclusão em 05/03" for somebody whose exclusion is on the 31st: the label
 * from one change and the date from another. Handing back the single delta the
 * row is about makes that combination unrepresentable.
 *
 * The list is re-sorted here rather than trusted to arrive ordered. It usually
 * does -- {@link scheduledChanges} sorts before {@link scheduledByCpf} groups --
 * but "usually" is not a property worth resting a coverage-end date on.
 */
export function primaryScheduledChange(deltas: readonly DeltaResponse[]): DeltaResponse | null {
  const terminations = deltas.filter((delta) => delta.action === DeltaAction.TERMINATION);
  const candidates = terminations.length > 0 ? terminations : deltas;
  return candidates.toSorted((a, b) => a.effective_date.localeCompare(b.effective_date))[0] ?? null;
}
